import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EMPLOYEE_KIND_LABEL, PAYROLL_KIND_LABEL } from '@masamune/shared';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { ActionsMenu } from '../../../components/ui/ActionsMenu';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow, SkeletonTable } from '../../../components/ui/Skeleton';
import {
  OrdinaFiltra, perData, perNumero, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../../components/ui/OrdinaFiltra';
import { fmtDate, fmtEur } from '../../../lib/utils';
import { ADMIN_PATH, adminHref, canSeeAdminPage } from '../../../lib/adminRoutes';
import { daysFromToday, plurale } from '../adminApi';
import { AdminHeader, StoricoNote, useStorico } from '../shared/AdminShell';
import { apriSchedaPersona } from '../../../components/modals/NewEmployeeModal';
import { dipendentiQuery, useDipendentiActions, type Chiusa, type DipendentiData, type PagamentoPersona, type Persona, type Verifica } from './dipendentiApi';
import '../admin.css';
import './dipendenti.css';

/**
 * Dipendenti (`/admin/dipendenti`): chi è in busta paga, quanto riceve, cosa
 * è stato pagato e quali versamenti non si vedono in banca.
 *
 * Solo con AMMINISTRAZIONE >= ADMIN (pagina ed endpoint): stipendi e compensi
 * non sono per chiunque legga l'Amministrazione.
 *
 * Tutti gli importi sono PAGAMENTI OSSERVATI in banca, non il costo aziendale
 * ufficiale (SPEC, correzione 7): lo dice la nota sotto la fascia. L'F24 del 16
 * entra nell'uscita mensile ma la sua cifra vive in Fisco (regola R3): qui
 * c'è solo il rimando.
 *
 * Blocchi, in ordine: frase calcolata, fascia (uscita mensile, persone in
 * busta paga, versamenti da verificare), persone, versamenti da verificare,
 * il mese tipo (solo parole e giorni), storico dei pagamenti (chiuso).
 * I collaboratori con fattura NON stanno qui (risposta di Lorenzo, 24/9).
 */
export function DipendentiPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const allowed = canSeeAdminPage(user, 'dipendenti');
  const q = useQuery({ ...dipendentiQuery, enabled: allowed });
  const d = q.data;

  const aggiungi = (
    <button className="btn btn-primary" onClick={() => apriSchedaPersona()}>
      <Icon name="plus" /> Nuova persona in busta paga
    </button>
  );

  if (!allowed) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Dipendenti" sub="Chi è in busta paga, quanto riceve e quali versamenti mancano." />
        <EmptyState
          icon="lock"
          title="Pagina dei dipendenti riservata"
          description="Stipendi e compensi li vede solo chi ha il livello Admin sull'Amministrazione."
          action={<button className="btn" onClick={() => navigate(ADMIN_PATH.situazione)}>Torna alla Situazione</button>}
        />
      </div>
    );
  }

  return (
    <div className="page adm-page dip-page">
      <AdminHeader title="Dipendenti" sub="Chi è in busta paga, quanto riceve e quali versamenti mancano." primary={aggiungi} />

      {q.isError ? (
        <EmptyState icon="alert" title="I dati dei dipendenti non sono arrivati" description="Riprova tra qualche secondo." action={<button className="btn" onClick={() => q.refetch()}>Riprova</button>} />
      ) : !d ? (
        <>
          <SkeletonKPIRow count={3} />
          <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={3} cols={6} withAvatar={false} /></tbody></table></div>
        </>
      ) : (
        <>
          <p className="t-body-lg dip-frase">{frase(d)}</p>
          <Fascia d={d} onVerifiche={() => document.getElementById('dip-verifiche')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <div className="adm-note">
            <Icon name="help" size={12} className="mute" />
            <span>
              Sono i <strong>pagamenti visti in banca</strong>, non il costo aziendale ufficiale: quello lo manda il consulente del lavoro con le buste paga.
            </span>
          </div>
          <Persone d={d} />
          <Verifiche d={d} />
          <MeseTipo d={d} />
          <Storico d={d} />
        </>
      )}

      <div className="m-actionbar m-only">{aggiungi}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Parole
// ════════════════════════════════════════════════════════════════════

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

/** "2026-08" -> "agosto" */
const nomeMese = (ym: string) => MESI[Number(ym.slice(5, 7)) - 1] ?? ym;
/** "2026-08" -> "agosto 2026" */
const meseAnno = (ym: string) => `${nomeMese(ym)} ${ym.slice(0, 4)}`;
/** "a" o "ad" davanti al mese (ad agosto, ad aprile, a maggio). */
const aMese = (ym: string) => (nomeMese(ym).startsWith('a') ? 'ad' : 'a');
/** Il mese, con l'anno solo se non è quello di oggi. */
const meseCorto = (ym: string, annoOggi: string) => (ym.slice(0, 4) === annoOggi ? nomeMese(ym) : meseAnno(ym));

/** "da gennaio 2025 a febbraio 2026", "da giugno ad agosto 2026", "agosto 2026". */
function periodo(from: string, to: string): string {
  if (from === to) return meseAnno(from);
  if (from.slice(0, 4) === to.slice(0, 4)) return `da ${nomeMese(from)} ${aMese(to)} ${meseAnno(to)}`;
  return `da ${meseAnno(from)} ${aMese(to)} ${meseAnno(to)}`;
}

/** "Ylenia e Lorenzo", "Ylenia, Lorenzo e Andrea". */
function elenco(xs: string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} e ${xs.at(-1)}`;
}

/** Il nome di battesimo, o nome e cognome se due persone si chiamano uguale. */
function nomiBrevi(persone: Persona[]): Map<string, string> {
  const primo = (p: Persona) => p.fullName.split(' ')[0] ?? p.fullName;
  const conta = new Map<string, number>();
  for (const p of persone) conta.set(primo(p), (conta.get(primo(p)) ?? 0) + 1);
  return new Map(persone.map((p) => [p.id, (conta.get(primo(p)) ?? 0) > 1 ? p.fullName : primo(p)]));
}

/** "oggi", "ieri", "5 giorni fa", "3 settimane fa", "6 mesi fa", "1 anno fa". */
function tempoFa(iso: string | null | undefined): string {
  const n = daysFromToday(iso);
  if (n == null) return '';
  const g = -n;
  if (g < 0) return tempoTra(iso);
  if (g === 0) return 'oggi';
  if (g === 1) return 'ieri';
  if (g < 14) return `${g} giorni fa`;
  if (g < 60) return `${Math.round(g / 7)} settimane fa`;
  if (g < 548) return `${Math.round(g / 30.4)} mesi fa`;
  const anni = Math.round(g / 365);
  return anni === 1 ? '1 anno fa' : `${anni} anni fa`;
}

/** "tra 3 giorni", "domani". */
function tempoTra(iso: string | null | undefined): string {
  const n = daysFromToday(iso);
  if (n == null) return '';
  if (n === 0) return 'oggi';
  if (n === 1) return 'domani';
  if (n < 14) return `tra ${n} giorni`;
  if (n < 60) return `tra ${Math.round(n / 7)} settimane`;
  return `tra ${Math.round(n / 30.4)} mesi`;
}

/** "da 1 anno", "da 8 mesi": da quanto dura una cosa iniziata in `iso`. */
function daQuanto(iso: string | null | undefined): string {
  const n = daysFromToday(iso);
  if (n == null || n > 0) return '';
  const g = -n;
  if (g < 45) return `da ${g} giorni`;
  if (g < 365) return `da ${Math.max(2, Math.round(g / 30.4))} mesi`;
  const anni = Math.round(g / 365);
  return anni === 1 ? 'da 1 anno' : `da ${anni} anni`;
}

const euro = (n: number) => `€ ${fmtEur(n)}`;

/** Il primo mese di stipendio che la banca ha visto per la persona ("2023-06"), se non c'è la data di assunzione. */
const primoStipendio = (p: Persona): string | null =>
  p.pagamenti.filter((x) => x.kind === 'STIPENDIO' && x.month).map((x) => x.month!).sort()[0] ?? null;

// Tipo di rapporto e cosa paga un bonifico: i nomi stanno in @masamune/shared
// (EMPLOYEE_KIND_LABEL, PAYROLL_KIND_LABEL), gli stessi della scheda persona e
// di Banca › Movimenti del conto.

// ════════════════════════════════════════════════════════════════════
// Frase in cima (calcolata, mai scritta fissa)
// ════════════════════════════════════════════════════════════════════

function frase(d: DipendentiData): ReactNode {
  const attivi = d.persone.filter((p) => p.active);
  if (!attivi.length) return <>Per ora non c'è nessuna persona in busta paga: si aggiunge con <strong>Nuova persona in busta paga</strong>.</>;
  const nomi = nomiBrevi(d.persone);
  const anno = d.today.slice(0, 4);

  // Stipendi: chi è pagato fino a quale mese, dal più recente.
  const perMese = new Map<string, string[]>();
  const maiVisti: string[] = [];
  for (const p of attivi) {
    const m = p.ultimoStipendio?.month;
    if (!m) { maiVisti.push(nomi.get(p.id)!); continue; }
    perMese.set(m, [...(perMese.get(m) ?? []), nomi.get(p.id)!]);
  }
  const gruppi = [...perMese.entries()].sort(([a], [b]) => b.localeCompare(a));
  const pagati = gruppi.map(([m, chi], i) => (
    <Fragment key={m}>
      {i > 0 && ' e '}
      fino {aMese(m)} <strong>{meseCorto(m, anno)}</strong> per {elenco(chi)}
    </Fragment>
  ));

  const aperte = d.verifiche.filter((v) => v.stato === 'DA_VERIFICARE');
  const recuperare = d.verifiche.filter((v) => v.stato === 'DA_RECUPERARE');
  const cosa = (vs: Verifica[]) => {
    const tfr = [...new Set(vs.filter((v) => v.kind === 'TFR').map((v) => nomi.get(v.employeeId) ?? v.fullName))];
    const stip = [...new Map(vs.filter((v) => v.kind === 'STIPENDIO').map((v) => [v.employeeId, v])).values()];
    const pezzi: string[] = [];
    if (tfr.length) pezzi.push(`alcuni mesi di TFR di ${elenco(tfr)} che in banca non risultano versati`);
    for (const v of stip) pezzi.push(`lo stipendio di ${nomi.get(v.employeeId) ?? v.fullName} da ${meseCorto(v.from, anno)}`);
    return elenco(pezzi);
  };

  return (
    <>
      Con i dati della banca di oggi, gli stipendi risultano pagati {pagati}.
      {maiVisti.length > 0 && <> Per {elenco(maiVisti)} in banca non c'è ancora nessuno stipendio.</>}
      {aperte.length > 0 ? <> Da verificare: {cosa(aperte)}.</> : recuperare.length === 0 && <> Nessun versamento da verificare.</>}
      {recuperare.length > 0 && <> Segnati da recuperare: {cosa(recuperare)}.</>}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Fascia
// ════════════════════════════════════════════════════════════════════

function Fascia({ d, onVerifiche }: { d: DipendentiData; onVerifiche: () => void }) {
  const navigate = useNavigate();
  const f = d.fascia;
  const r = f.perRapporto;
  const composizione = elenco([
    r.DIPENDENTE ? plurale(r.DIPENDENTE, 'dipendente', 'dipendenti') : '',
    r.APPRENDISTA ? plurale(r.APPRENDISTA, 'apprendista', 'apprendisti') : '',
    r.AMMINISTRATORE === 1 ? "l'amministratore" : r.AMMINISTRATORE ? plurale(r.AMMINISTRATORE, 'amministratore', 'amministratori') : '',
  ].filter(Boolean));
  const tipi = new Set(d.verifiche.map((v) => v.kind));
  const cosaManca = tipi.size === 2 ? 'TFR senza bonifico e stipendi non pagati' : tipi.has('TFR') ? 'TFR senza bonifico ad Alleanza' : tipi.has('STIPENDIO') ? 'stipendi non pagati' : 'nessuno, con i dati di oggi';

  return (
    <div className="stat-band">
      <div>
        <div className="st-label">
          Uscita mensile per il personale (stima)
          <Helper placement="bottom">
            <strong>Come si calcola</strong>
            <p className="mt-1">
              È la somma di tre cose, tutte prese dai pagamenti visti in banca: gli stipendi netti medi delle persone
              qui sotto (per l'amministratore netto e rimborsi km insieme, perché la causale non li separa), il TFR
              versato ogni mese ad Alleanza e la media degli F24 del 16 pagati dal {meseAnno(d.daMese)}
              {f.f24Mesi ? ` (${plurale(f.f24Mesi, 'mese', 'mesi')})` : ''}, cioè le tasse e i contributi trattenuti in busta.
            </p>
            <p className="mt-1">Non è il costo aziendale ufficiale: quello lo dà il consulente del lavoro.</p>
          </Helper>
        </div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={f.uscitaMensile} format={(n) => fmtEur(n)} /></div>
        <div className="st-delta">
          stipendi netti, TFR e tasse sugli stipendi ·{' '}
          <a className="underline cursor-pointer" onClick={() => navigate(adminHref('fisco', { vista: 'f24' }))}>F24 del 16 in Fisco ›</a>
        </div>
      </div>
      <div>
        <div className="st-label">Persone in busta paga</div>
        <div className="st-value"><AnimatedNumber value={f.inBustaPaga} /></div>
        <div className="st-delta">{composizione || 'nessuna, per ora'}</div>
      </div>
      <div className={f.verifiche ? 'st-click' : undefined} role={f.verifiche ? 'button' : undefined} tabIndex={f.verifiche ? 0 : undefined}
        onClick={f.verifiche ? onVerifiche : undefined} onKeyDown={(e) => { if (f.verifiche && e.key === 'Enter') onVerifiche(); }}>
        <div className="st-label">Versamenti da verificare</div>
        <div className="st-value"><AnimatedNumber value={f.verifiche} /></div>
        <div className="st-delta">{cosaManca}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Persone
// ════════════════════════════════════════════════════════════════════

function statoPersona(p: Persona): { tone: 'ok' | 'warn' | 'alert' | 'ghost'; label: string } {
  if (!p.active) return { tone: 'ghost', label: 'Rapporto terminato' };
  const { tfr, stipendio, daRecuperare } = p.verificheAperte;
  const tot = tfr + stipendio;
  if (!tot) return { tone: 'ok', label: 'Tutto pagato' };
  const come = daRecuperare === tot ? 'da recuperare' : 'da verificare';
  const cosa = stipendio && tfr ? 'Stipendi e TFR' : stipendio ? 'Stipendi' : 'TFR';
  return { tone: daRecuperare === tot ? 'alert' : 'warn', label: `${cosa} ${come}` };
}

function Persone({ d }: { d: DipendentiData }) {
  const { riprendi } = useDipendentiActions();
  const anno = d.today.slice(0, 4);
  if (!d.persone.length) {
    return (
      <div className="ed-section">
        <EmptyState icon="user" title="Nessuna persona in busta paga" description="Aggiungi chi ha la busta paga, con i nomi con cui compare nei bonifici: stipendi, rimborsi e TFR si collegano da soli." />
      </div>
    );
  }
  return (
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Persone in busta paga</span>
        <span className="mute text-[12px]">medie dei mesi pagati dal {meseAnno(d.daMese)}</span>
      </div>
      <table className="tbl tbl--ed tbl--card tbl--static">
        <thead>
          <tr>
            <th>Persona</th>
            <th style={{ width: 210 }}>Tipo di rapporto</th>
            <th className="num" style={{ width: 190 }}>Stipendio netto al mese</th>
            <th className="num" style={{ width: 140 }}>TFR versato al mese</th>
            <th style={{ width: 180 }}>Ultimo stipendio pagato</th>
            <th style={{ width: 210 }}>Stato di stipendi e TFR</th>
            <th style={{ width: 44 }} />
          </tr>
        </thead>
        <tbody>
          {d.persone.map((p) => {
            const stato = statoPersona(p);
            const u = p.ultimoStipendio;
            const titoloMedia = p.netto.media != null
              ? `Media di ${plurale(p.netto.mesi.length, 'mese pagato', 'mesi pagati')} per intero: ${p.netto.mesi.map(nomeMese).join(', ')}.`
                + (p.netto.esclusi.length ? ` Fuori dalla media ${elenco(p.netto.esclusi.map(nomeMese))}: il bonifico porta dentro anche un rimborso.` : '')
              : 'Nessun mese pagato per intero da quando si guarda.';
            return (
              <tr key={p.id} className={p.active ? undefined : 'dip-finita'}>
                <td data-m="hd" className="cell-2l">
                  <div className="p">{p.fullName}</div>
                  <div className="s" title={p.hiredAt ? `Assunzione: ${fmtDate(p.hiredAt)}` : undefined}>
                    {!p.active && p.endedAt
                      ? `rapporto finito ${tempoFa(p.endedAt)}`
                      : p.hiredAt ? `in azienda ${daQuanto(p.hiredAt)}` : primoStipendio(p) ? `primo stipendio in banca: ${meseAnno(primoStipendio(p)!)}` : p.bankNames.length ? 'nessuno stipendio in banca' : 'manca il nome con cui compare in banca'}
                  </div>
                </td>
                <td data-m="key" data-l="Tipo di rapporto" className="cell-2l">
                  <div>{EMPLOYEE_KIND_LABEL[p.kind]}</div>
                  {p.partTimePct != null && p.partTimePct < 100 && <div className="s">part time {p.partTimePct}%</div>}
                </td>
                <td data-m="key" data-l={p.netto.insieme ? 'Netto e rimborsi km al mese' : 'Stipendio netto al mese'} className="num cell-2l" title={titoloMedia}>
                  <div className="mono font-semibold">{p.netto.media != null ? euro(p.netto.media) : '—'}</div>
                  {p.netto.insieme && <div className="s">netto e rimborsi km insieme</div>}
                  {!p.netto.insieme && p.netto.esclusi.length > 0 && <div className="s">{elenco(p.netto.esclusi.map(nomeMese))} fuori media: c'era un rimborso</div>}
                </td>
                <td data-m="key" data-l="TFR versato al mese" className="num mono" title={p.tfrAlMese == null ? 'Nessun TFR ad Alleanza per questa persona' : 'La spesa fissa del TFR su Alleanza'}>
                  {p.tfrAlMese != null ? euro(p.tfrAlMese) : '—'}
                </td>
                <td data-m="key" data-l="Ultimo stipendio pagato" className="cell-2l" title={u?.paidOn ? `Bonifico del ${fmtDate(u.paidOn)}` : undefined}>
                  <div>{u?.month ? meseCorto(u.month, anno) : 'nessuno'}</div>
                  {u?.paidOn && <div className="s">{tempoFa(u.paidOn)}</div>}
                </td>
                <td data-m="key" data-l="Stato di stipendi e TFR">
                  <Chip tone={stato.tone} dot>{stato.label}</Chip>
                </td>
                <td data-m="act" onClick={(e) => e.stopPropagation()}>
                  <ActionsMenu
                    label={`Azioni su ${p.fullName}`}
                    items={[
                      { key: 'edit', label: 'Modifica la scheda', icon: 'pencil', onClick: () => apriSchedaPersona(p, 'modifica') },
                      { key: 'end', label: 'Termina il rapporto', icon: 'logout', hidden: !p.active, onClick: () => apriSchedaPersona(p, 'termina') },
                      { key: 'resume', label: 'Il rapporto è ancora in corso', icon: 'undo', hidden: p.active, onClick: () => riprendi.mutate({ id: p.id, nome: p.fullName }) },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Versamenti da verificare
// ════════════════════════════════════════════════════════════════════

function Verifiche({ d }: { d: DipendentiData }) {
  const { decidi } = useDipendentiActions();
  const [aperto, setAperto] = useState<string | null>(null);
  const [chiuseAperte, setChiuseAperte] = useState(false);
  const persona = (id: string) => d.persone.find((p) => p.id === id);
  const anno = d.today.slice(0, 4);

  return (
    <div className="ed-section" id="dip-verifiche">
      <div className="data-hd">
        <span className="t-micro">Versamenti da verificare: TFR e stipendi</span>
        <span className="mute text-[12px]">
          ogni decisione resta scritta, con nome e data
          <Helper placement="left">
            <strong>Cosa fare con ogni voce</strong>
            <p className="mt-1"><strong>Segna pagato altrove</strong>: il versamento c'è, ma non con un bonifico riconoscibile (per esempio l'ha fatto il consulente). La voce si chiude con la tua nota.</p>
            <p className="mt-1"><strong>Segna da recuperare</strong>: il versamento manca davvero. La voce resta qui come da recuperare finché non arriva il bonifico.</p>
            <p className="mt-1">Gli importi sono stime: i mesi per l'importo al mese di oggi, meno gli acconti già pagati.</p>
          </Helper>
        </span>
      </div>

      {d.verifiche.length === 0 ? (
        <div className="adm-note">
          <Icon name="check" size={12} className="mute" />
          <span>Con i dati della banca di oggi stipendi e TFR risultano tutti versati.</span>
        </div>
      ) : (
        <table className="tbl tbl--ed tbl--card tbl--static">
          <thead>
            <tr>
              <th>Versamento mancante</th>
              <th>Cosa dice la banca</th>
              <th className="num" style={{ width: 130 }}>Importo stimato</th>
              <th style={{ width: 160 }}>Stato della verifica</th>
              <th style={{ width: 290 }} />
            </tr>
          </thead>
          <tbody>
            {d.verifiche.map((v) => {
              const p = persona(v.employeeId);
              const n = v.months.length;
              const acconti = v.acconti.reduce((s, a) => s + a.amount, 0);
              const titoloStima = v.stima == null ? undefined : v.kind === 'TFR'
                ? `${plurale(n, 'mese', 'mesi')} per il TFR al mese di oggi${p?.tfrAlMese != null ? ` (${euro(p.tfrAlMese)})` : ''}: la cifra vera la dà il consulente del lavoro.`
                : `${plurale(n, 'mese', 'mesi')} per la media mensile${p?.netto.media != null ? ` (${euro(p.netto.media)})` : ''}${acconti ? `, meno ${euro(acconti)} di acconti già pagati` : ''}.`;
              return (
                <tr key={v.key}>
                  <td data-m="hd" className="cell-2l">
                    <div className="p">{v.kind === 'TFR' ? 'TFR' : 'Stipendio'} di {v.fullName}</div>
                    <div className="s">{periodo(v.from, v.to)}{n > 1 ? `, ${plurale(n, 'mese', 'mesi')}` : ''}</div>
                  </td>
                  <td data-m="row" data-l="Cosa dice la banca" className="text-[13px]">
                    <CosaDiceLaBanca v={v} p={p} anno={anno} />
                  </td>
                  <td data-m="key" data-l="Importo stimato" className="num mono dip-nowrap" title={titoloStima}>
                    {/* Un mese solo di TFR vale quanto il "TFR versato al mese" della
                        tabella sopra: la cifra non si ripete (regola R1), si dice a parole. */}
                    {v.kind === 'TFR' && n === 1 && p?.tfrAlMese != null
                      ? <span className="mute">un mese di TFR</span>
                      : v.stima != null ? `circa ${euro(v.stima)}` : '—'}
                  </td>
                  <td data-m="key" data-l="Stato della verifica" className="cell-2l">
                    {v.stato === 'DA_RECUPERARE' ? <Chip tone="alert" dot>Da recuperare</Chip> : <Chip tone="warn" dot>Da verificare</Chip>}
                    {v.decisoIl && <div className="s" title={fmtDate(v.decisoIl)}>{v.decisoDa ? `${v.decisoDa}, ` : ''}{tempoFa(v.decisoIl)}</div>}
                  </td>
                  <td data-m="row" onClick={(e) => e.stopPropagation()}>
                    <div className="dip-azioni">
                      <button className="btn btn-sm" onClick={() => setAperto(aperto === v.key ? null : v.key)} disabled={decidi.isPending}>
                        Segna pagato altrove
                      </button>
                      {v.stato === 'DA_VERIFICARE' ? (
                        <button className="btn btn-sm btn-ghost" disabled={decidi.isPending}
                          title="Il versamento manca davvero: la voce resta qui come da recuperare"
                          onClick={() => decidi.mutate({ employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'DA_RECUPERARE' })}>
                          Segna da recuperare
                        </button>
                      ) : (
                        <button className="btn btn-sm btn-ghost" disabled={decidi.isPending}
                          onClick={() => decidi.mutate({ employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'RIAPERTA' })}>
                          Rimetti da verificare
                        </button>
                      )}
                      {aperto === v.key && (
                        <AltrovePopover
                          titolo={`${v.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${v.fullName}, ${periodo(v.from, v.to)}`}
                          pending={decidi.isPending}
                          onClose={() => setAperto(null)}
                          onConfirm={(note) => decidi.mutate(
                            { employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'ALTROVE', note },
                            { onSuccess: () => setAperto(null) },
                          )}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {d.chiuse.length > 0 && (
        <div className="adm-fold">
          <button className="btn btn-sm btn-ghost" aria-expanded={chiuseAperte} onClick={() => setChiuseAperte((x) => !x)}>
            <Icon name={chiuseAperte ? 'chevronU' : 'chevronD'} size={10} /> Versamenti chiusi perché pagati altrove ({d.chiuse.length})
          </button>
          {chiuseAperte && (
            <div className="dip-chiuse">
              {d.chiuse.map((c) => <RigaChiusa key={c.key} c={c} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CosaDiceLaBanca({ v, p, anno }: { v: Verifica; p: Persona | undefined; anno: string }) {
  if (v.kind === 'TFR') {
    if (v.months.length > 1) {
      return <>Nessun bonifico ad Alleanza con la sua polizza{p?.tfrRef ? ` (n. ${p.tfrRef})` : ''} per questi mesi.</>;
    }
    return (
      <span title={`Doveva partire intorno al ${fmtDate(v.dovevaPartire)}`}>
        Il TFR di solito parte intorno al {Number(v.dovevaPartire.slice(8, 10))} del mese dopo: questo doveva partire {tempoFa(v.dovevaPartire)} e in banca non c'è.
      </span>
    );
  }
  const ult = p?.ultimoStipendio?.month;
  const soloAcconto = v.acconti.map((a) => nomeMese(a.month));
  return (
    <>
      {ult ? <>L'ultimo mese pagato per intero è {meseCorto(ult, anno)}.</> : <>In banca non c'è nessuno stipendio pagato per intero.</>}
      {soloAcconto.length > 0 && <> Di {elenco(soloAcconto)} è arrivato solo un acconto.</>}
    </>
  );
}

function RigaChiusa({ c }: { c: Chiusa }) {
  const { decidi } = useDipendentiActions();
  return (
    <div className="adm-line">
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{c.kind === 'TFR' ? 'TFR' : 'Stipendio'} di {c.fullName}, {periodo(c.from, c.to)}</div>
        <div className="text-[12px] mute">
          {c.nota ?? 'senza nota'}
          {c.decisoIl && <span title={fmtDate(c.decisoIl)}> · chiuso {c.decisoDa ? `da ${c.decisoDa} ` : ''}{tempoFa(c.decisoIl)}</span>}
        </div>
      </div>
      <button className="btn btn-sm btn-ghost" disabled={decidi.isPending}
        onClick={() => decidi.mutate({ employeeId: c.employeeId, kind: c.kind, months: c.months, resolution: 'RIAPERTA' })}>
        Rimetti da verificare
      </button>
    </div>
  );
}

/** "Segna pagato altrove": una nota obbligatoria, perché fra sei mesi qualcuno chiederà dove. */
function AltrovePopover({ titolo, pending, onConfirm, onClose }: {
  titolo: string;
  pending: boolean;
  onConfirm: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const mobile = useIsMobile();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const ok = note.trim().length >= 3 && !pending;
  const submit = () => { if (ok) onConfirm(note.trim()); };
  const corpo = (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Segna pagato altrove"
        style={{ width: 'min(380px, calc(100vw - 32px))' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}>
        <div className="adm-pop-hd">
          <span className="t-micro">Segna pagato altrove</span>
        </div>
        <div className="px-4 pt-3 text-[13px] font-semibold">{titolo}</div>
        <div className="px-4 pt-2 pb-1">
          <label className="field-label">Come o dove è stato pagato</label>
          <input className="inp inp-sm w-full" autoFocus placeholder="Es. versato dal consulente, pagato in contanti" value={note} onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        </div>
        <div className="px-4 pb-3 pt-1 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          La voce esce dai versamenti da verificare e resta fra quelli chiusi, con la tua nota. Si può riaprire.
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={!ok} onClick={submit}>Segna pagato altrove</button>
        </div>
      </div>
    </>
  );
  // Sul telefono mobile.css ne fa un foglio dal basso (position: fixed). Dentro
  // la scheda della tabella resterebbe sotto la barra in basso (la riga fa da
  // contesto di impilamento): lo si porta in fondo al documento.
  return mobile ? createPortal(corpo, document.body) : corpo;
}

// ════════════════════════════════════════════════════════════════════
// Il mese tipo: solo parole e giorni
// ════════════════════════════════════════════════════════════════════

function MeseTipo({ d }: { d: DipendentiData }) {
  const navigate = useNavigate();
  const nomi = nomiBrevi(d.persone);
  const perNome = new Map(d.persone.map((p) => [p.fullName, p]));
  const breve = (fullName: string) => nomi.get(perNome.get(fullName)?.id ?? '') ?? fullName;

  const righe: Array<{ icon: IconName; cosa: string; quando: string; link?: { label: string; to: string } }> = [];
  const fissi = new Map<number, string[]>();
  for (const s of d.meseTipo.stipendi) {
    if (s.fisso) fissi.set(s.giorno, [...(fissi.get(s.giorno) ?? []), breve(s.nome)]);
  }
  for (const [g, chi] of fissi) {
    righe.push({ icon: 'banknote', cosa: `${chi.length > 1 ? 'Stipendi' : 'Stipendio'} di ${elenco(chi)}`, quando: `un bonifico intorno al ${g} del mese dopo` });
  }
  for (const s of d.meseTipo.stipendi.filter((x) => !x.fisso)) {
    righe.push({
      icon: 'banknote',
      cosa: `Stipendio di ${breve(s.nome)}`,
      quando: s.stimato ? 'nessun saldo in banca da cui capire il giorno' : 'in più bonifici, fra acconti e saldo, senza un giorno fisso',
    });
  }
  const conTfr = d.persone.filter((p) => p.active && p.tfrAlMese != null).map((p) => nomi.get(p.id)!);
  if (conTfr.length) {
    righe.push({ icon: 'coins', cosa: `TFR di ${elenco(conTfr)} ad Alleanza`, quando: `intorno al ${d.meseTipo.tfrGiorno ?? 10} del mese dopo` });
  }
  righe.push({
    icon: 'percent',
    cosa: 'F24 con le tasse e i contributi trattenuti in busta',
    quando: `entro il ${d.meseTipo.f24Giorno} del mese dopo`,
    link: { label: 'F24 del 16 in Fisco', to: adminHref('fisco', { vista: 'f24' }) },
  });
  if (d.meseTipo.jetHrGiorno) {
    righe.push({
      icon: 'file',
      cosa: 'Buste paga preparate da Jet HR',
      quando: `addebito intorno al ${d.meseTipo.jetHrGiorno} del mese`,
      link: { label: 'Spese fisse', to: adminHref('spese', { vista: 'fisse' }) },
    });
  }

  return (
    <div className="ed-section">
      <div className="data-hd">
        <span className="t-micro">Il mese tipo: quando partono stipendi, TFR e F24</span>
        <span className="mute text-[12px]">dai bonifici dal {meseAnno(d.daMese)}</span>
      </div>
      <div className="dip-mese">
        {righe.map((r) => (
          <div key={r.cosa} className="adm-line">
            <Icon name={r.icon} size={14} className="mute" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold">{r.cosa}</span>
              <span className="mute">: {r.quando}</span>
            </div>
            {r.link && (
              <a className="text-[12px] underline cursor-pointer" onClick={() => navigate(r.link!.to)}>{r.link.label} ›</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Storico dei pagamenti (chiuso)
// ════════════════════════════════════════════════════════════════════

/** Il TFR pagato in blocco cita più mesi in causale: "fino a dicembre 2023" invece di "dicembre 2023". */
const RE_PIU_MESI = /\b(gen|feb|mar|apr|mag|giu|lug|ago|set|ott|nov|dic)[a-z]*\b[^]*\b(gen|feb|mar|apr|mag|giu|lug|ago|set|ott|nov|dic)[a-z]*\b/i;

/** Un bonifico dello storico con la persona a cui è andato: serve al filtro "A chi". */
type BonificoPersona = PagamentoPersona & { personaId: string };

/** Ordina e filtra lo storico (24/9): nel browser, dentro il blocco di ogni persona. */
const ORDINI_STORICO: OpzioneOrdina<BonificoPersona>[] = [
  { key: 'recenti', label: 'Bonifici più recenti prima', criteri: [perData((x) => x.date, 'decrescente')] },
  { key: 'vecchi', label: 'Bonifici più vecchi prima', criteri: [perData((x) => x.date)] },
  { key: 'importo', label: 'Prima gli importi più alti', criteri: [perNumero((x) => x.amount, 'decrescente'), perData((x) => x.date, 'decrescente')] },
];

function Storico({ d }: { d: DipendentiData }) {
  const storico = useStorico();
  const ordina = useOrdina(ORDINI_STORICO);
  const tutti = useMemo<BonificoPersona[]>(() => d.persone.flatMap((p) => p.pagamenti.map((x) => ({ ...x, personaId: p.id }))), [d]);
  const filtriDefs = useMemo<FiltroDef<BonificoPersona>[]>(() => [
    {
      param: 'pagamento', label: 'Cosa paga il bonifico', multi: true,
      opzioni: (Object.keys(PAYROLL_KIND_LABEL) as Array<keyof typeof PAYROLL_KIND_LABEL>).map((k) => ({ value: k, label: PAYROLL_KIND_LABEL[k] })),
      test: (x, v) => x.kind === v,
    },
    {
      param: 'persona', label: 'A chi è andato', multi: true,
      opzioni: d.persone.filter((p) => p.pagamenti.length > 0).map((p) => ({ value: p.id, label: p.fullName })),
      test: (x, v) => x.personaId === v,
    },
  ], [d]);
  const filtri = useFiltri(filtriDefs, tutti);
  // Con un filtro o un ordine nell'URL (link condiviso) il blocco parte aperto.
  const [open, setOpen] = useState(filtri.attivi > 0 || !ordina.predefinita);
  const da = `${d.daMese}-01`;
  const conPagamenti = d.persone
    .map((p) => ({ p, righe: ordina.applica(filtri.applica(p.pagamenti.map((x) => ({ ...x, personaId: p.id }))).filter((x) => storico.on || (x.date ?? '') >= da)) }))
    .filter((x) => x.righe.length > 0);
  const nascosti = d.persone.reduce((s, p) => s + p.pagamenti.filter((x) => (x.date ?? '') < da).length, 0);

  return (
    <div className="adm-fold">
      <button className="btn btn-sm btn-ghost" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <Icon name={open ? 'chevronU' : 'chevronD'} size={10} /> {open ? 'Nascondi' : 'Mostra'} i pagamenti a ogni persona, dalla banca
      </button>
      {open && (
        <div className="dip-storico">
          <div className="flex items-center gap-3 flex-wrap">
            {storico.on
              ? <StoricoNote year={storico.year} onOff={() => storico.set(false)} />
              : nascosti > 0 && <span className="text-[12px] mute">Dal {meseAnno(d.daMese)}. Prima ce ne sono altri {nascosti}.</span>}
            {!storico.on && nascosti > 0 && storico.button}
            <span className="flex-1" />
            <OrdinaFiltra ordina={ordina} filtri={filtri} />
          </div>
          {conPagamenti.length === 0 && (
            <div className="adm-note">
              {filtri.attivi > 0
                ? <span>Nessun pagamento con questi filtri. <a className="underline cursor-pointer" onClick={filtri.clear}>Togli i filtri</a></span>
                : <span>Nessun pagamento riconosciuto in banca.</span>}
            </div>
          )}
          {conPagamenti.map(({ p, righe }) => (
            <div key={p.id} className="ed-section">
              <div className="data-hd">
                <span className="t-micro">Pagamenti a {p.fullName}</span>
                <span className="mute text-[12px]">{plurale(righe.length, 'bonifico', 'bonifici')}</span>
              </div>
              <table className="tbl tbl--ed tbl--card tbl--static">
                <thead>
                  <tr>
                    <th style={{ width: 260 }}>Cosa paga il bonifico</th>
                    <th style={{ width: 150 }}>Data del bonifico</th>
                    <th>Causale del bonifico</th>
                    <th className="num" style={{ width: 120 }}>Importo del bonifico</th>
                  </tr>
                </thead>
                <tbody>
                  {righe.map((x) => (
                    <tr key={x.id}>
                      <td data-m="hd" className="cell-2l">
                        <div className="p">{PAYROLL_KIND_LABEL[x.kind]}</div>
                        {x.month && x.kind !== 'ALTRO' && (
                          <div className="s">
                            {x.kind === 'TFR' && RE_PIU_MESI.test(x.causale) ? `fino a ${meseAnno(x.month)}` : x.kind === 'RIMBORSO_KM' ? `pagato ${aMese(x.month)} ${meseAnno(x.month)}` : `di ${meseAnno(x.month)}`}
                          </div>
                        )}
                      </td>
                      <td data-m="key" data-l="Data del bonifico" className="mute" title={x.date ? fmtDate(x.date) : undefined}>{tempoFa(x.date)}</td>
                      <td data-m="row" data-l="Causale del bonifico" className="adm-clip"><div className="mute" title={x.causale}>{x.causale || '—'}</div></td>
                      <td data-m="key" data-l="Importo del bonifico" className="num mono">{euro(x.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
